import * as Effects from 'redux-saga/effects';
import * as apiService from './service';
import {
    ensureReconciliations,
    fetchReconciliationsSucceeded,
    fetchReconciliationsFailed,
    ensureDrillDown,
    fetchDrillDownSucceeded,
    fetchDrillDownFailed,
    ensureAvailability,
    fetchAvailabilitySucceeded,
    fetchAvailabilityFailed,
} from './reconciliationsSlice';
import { selectReconciliationsEntry, selectDrillDownEntry, selectAvailability } from './selectors';
import { mergeAvailability } from './mergeAvailability';

const call: any = Effects.call;
const put = Effects.put;
const select: any = Effects.select;
const takeEvery = Effects.takeEvery;

const UNIFIER_SERVICE_KEY = 'UW_GET_RECONCILIATIONS';

function unwrapReconciliations(body: any) {
    return body?.responseBody?.[UNIFIER_SERVICE_KEY]?.data?.data;
}

// The reducer claims the entry (loading + token) on dispatch, but takeEvery spawns one worker
// per action regardless of that outcome — so the worker itself must re-check the claim before
// fetching. Otherwise every mounted consumer (chip + up to 4 pills + drawer) fires its own
// network call, defeating the "one fetch per company/year-set" contract.
function* ensureReconciliationsSaga(action: ReturnType<typeof ensureReconciliations>) {
    const { orgId, fys, requestToken } = action.payload;
    const entry = yield select(selectReconciliationsEntry, orgId, fys);
    if (!entry || entry.token !== requestToken) return;
    try {
        const body = yield call(apiService.fetchReconciliations, orgId, fys, null);
        const payload = unwrapReconciliations(body);
        // Failure contract: a swallowed UW failure surfaces as responseCode 20 + an empty
        // object, so responseCode/HTTP status alone is not sufficient — treat "no overall and
        // no sections" as failure so the chip/pills never render a 0% for an upstream outage.
        if (!payload?.overall && !payload?.sections?.length) {
            yield put(fetchReconciliationsFailed({ orgId, fys, requestToken, error: 'Reconciliations unavailable' }));
            return;
        }
        yield put(fetchReconciliationsSucceeded({ orgId, fys, payload, requestToken }));
    } catch (err: any) {
        yield put(fetchReconciliationsFailed({ orgId, fys, requestToken, error: err?.message || 'Something went wrong' }));
    }
}

function* ensureDrillDownSaga(action: ReturnType<typeof ensureDrillDown>) {
    const { orgId, fys, reconId, fy, requestToken } = action.payload;
    const entry = yield select(selectDrillDownEntry, orgId, fys, reconId, fy);
    if (!entry || entry.token !== requestToken) return;
    try {
        const body = yield call(apiService.fetchReconciliations, orgId, fys, { reconId, fy });
        const payload = unwrapReconciliations(body);
        const rows = payload?.drillDown?.rows;
        if (!Array.isArray(rows)) {
            yield put(fetchDrillDownFailed({ orgId, fys, reconId, fy, requestToken, error: 'Drill-down unavailable' }));
            return;
        }
        yield put(fetchDrillDownSucceeded({ orgId, fys, reconId, fy, rows, requestToken }));
    } catch (err: any) {
        yield put(fetchDrillDownFailed({ orgId, fys, reconId, fy, requestToken, error: err?.message || 'Something went wrong' }));
    }
}

function* ensureAvailabilitySaga(action: ReturnType<typeof ensureAvailability>) {
    const { orgId, fy, window, requestToken } = action.payload;
    const entry = yield select(selectAvailability, orgId, fy, window);
    if (!entry || entry.token !== requestToken) return;
    try {
        const body = yield call(apiService.fetchDataAvailability, orgId, fy, window);
        // A failed leg arrives as `{}` inside HTTP 200 — a null merge is a failure, exactly like a transport error.
        const data = mergeAvailability(body?.responseBody);
        if (!data) {
            console.error('Data Availability response had a missing or malformed leg');
            yield put(fetchAvailabilityFailed({ orgId, fy, window, requestToken }));
            return;
        }
        yield put(fetchAvailabilitySucceeded({ orgId, fy, window, data, requestToken }));
    } catch (err: any) {
        console.error('Data Availability fetch failed:', err);
        yield put(fetchAvailabilityFailed({ orgId, fy, window, requestToken }));
    }
}

// takeEvery, NEVER takeLatest — takeLatest would cancel the in-flight default fetch when an
// explicit-FY ensure follows it, breaking the "both entries end success" de-dup contract
// De-dup is the reducer's claim (isClaimable) PLUS each worker's own re-check of it
// above — the watcher itself makes no dedup decision.
export default function* reconciliationsSagaWatcher() {
    yield takeEvery(ensureReconciliations.type, ensureReconciliationsSaga);
    yield takeEvery(ensureDrillDown.type, ensureDrillDownSaga);
    yield takeEvery(ensureAvailability.type, ensureAvailabilitySaga);
}
